/**
 * 69.101 (DECISIONS 416, the Lab's Callouts row; the sheet design_sheets/bot_callouts.html): the bots' callouts in play, on
 * a canvas of their own over the scene. At the shoulder, the shown sides' calls over their soldiers' heads — within
 * CALLOUT_LOOK.shoulderReach people of the camera and in front of it, at most `shoulderMost` (the highest rank, then the
 * nearest), a pip for the rest, stacked clear of each other. In the command view, one call a squad over its block (where
 * the command overlay drew it; squads merged into one block, one call between them), and for the squads picked, or every squad with the camera close in, the drill (a bound's
 * movers' arrows to their posts and its coverers' arcs toward the face; a play's support fire and its ring, a flank's
 * route) and the target mark (brackets on the enemy most of the squad is shooting at). Reads the state; writes nothing.
 */
import * as THREE from 'three';
import type { GameState, Vec3 } from '../types';
import { TEAMS } from '../constants';
import { PHYS_DT, PERSON_BODY_HEIGHT } from '../constants/physics';
import { personSetOf } from '../core/scaleProfile';
import { orderPlace } from '../core/orderForms';
import { getSurfaceRadius } from '../core/surface';
import { CALLOUTS, CALLOUT_LOOK, CALLOUT_TONE, CalloutWatch, calloutAlpha, calloutBox, drawCallout, stackCallouts, type Callout } from '../ui/botCallouts';

const RANK = new Map(CALLOUTS.map(r => [r.id, r.rank]));
/** The command view's drill for every squad with the camera within this many people of it (else the picked squads only). */
const DRILL_NEAR_PEOPLE = 160;

export interface CalloutFrame {
  state: GameState;
  camera: THREE.Camera;
  tick: number;
  /** the sides whose calls show (botCallouts.ts calloutTeams) */
  teams: readonly number[];
  view: 'shoulder' | 'command';
  /** the unit the viewer holds (it never calls) */
  viewerId: number | null;
  /** the command view: where each squad's block was drawn, the squads picked, the view's fade */
  blocks?: ReadonlyMap<number, { x: number; y: number }>;
  picked?: ReadonlySet<number>;
  alpha?: number;
}

export class CalloutOverlay {
  private canvas: HTMLCanvasElement;
  private watch = new CalloutWatch();
  private lastLook = -Infinity;
  private v = new THREE.Vector3();
  /** the look's cost (ms), the last look's — the frame budget's readout (136); the tags drawn this frame */
  lookMs = 0;
  shown = 0;

  constructor() {
    this.canvas = document.createElement('canvas');
    this.canvas.style.cssText = 'position:fixed; inset:0; width:100vw; height:100vh; pointer-events:none; z-index:20;';
    document.body.appendChild(this.canvas);
  }

  dispose(): void { this.canvas.remove(); }

  /**
   * Where a point on the ground (lifted by `lift`) lands on the `w`×`h` screen and how far it is from the camera — never
   * clamped (the command overlay's screenPlace holds one off the view at its edge: a call or an arc there would be drawn
   * where nobody stands); null behind the camera or over the planet's limb.
   */
  private at(state: GameState, camera: THREE.Camera, planetIndex: number, dir: Vec3, lift: number, w: number, h: number): { x: number; y: number; dist: number } | null {
    const p = state.planets[planetIndex];
    if (!p) return null;
    const r = getSurfaceRadius(p, dir) + lift, cam = camera.position;
    const x = p.position.x + dir.x * r, y = p.position.y + dir.y * r, z = p.position.z + dir.z * r;
    const dist = Math.hypot(x - cam.x, y - cam.y, z - cam.z);
    if ((cam.x - x) * dir.x + (cam.y - y) * dir.y + (cam.z - z) * dir.z < -0.2 * dist) return null;   // over the limb
    this.v.set(x, y, z).applyMatrix4(camera.matrixWorldInverse);
    if (this.v.z > 0) return null;   // behind the camera
    this.v.set(x, y, z).project(camera);
    return { x: (this.v.x + 1) / 2 * w, y: (1 - this.v.y) / 2 * h, dist };
  }
  hide(): void { this.canvas.style.display = 'none'; }

  update(f: CalloutFrame): void {
    if (f.tick - this.lastLook >= 1 / (CALLOUT_LOOK.perSecond * PHYS_DT) || f.tick < this.lastLook) {
      const t0 = performance.now();
      this.watch.look(f.state, f.tick, f.teams);
      this.lookMs = performance.now() - t0;
      this.lastLook = f.tick;
    }
    const dpr = Math.min(2, window.devicePixelRatio || 1), w = window.innerWidth, h = window.innerHeight;
    if (this.canvas.width !== Math.round(w * dpr) || this.canvas.height !== Math.round(h * dpr)) { this.canvas.width = Math.round(w * dpr); this.canvas.height = Math.round(h * dpr); }
    this.canvas.style.display = '';
    const g = this.canvas.getContext('2d')!;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, w, h);
    const live = this.watch.live.filter(c => f.teams.includes(c.team) && c.unitId !== f.viewerId);
    this.shown = 0;
    if (f.view === 'shoulder') this.shoulder(g, f, live, w, h);
    else this.command(g, f, live, w, h);
  }

  private shoulder(g: CanvasRenderingContext2D, f: CalloutFrame, live: Callout[], w: number, h: number): void {
    const { state } = f, person = PERSON_BODY_HEIGHT * personSetOf(state).k;
    const seen: Array<{ c: Callout; x: number; y: number; dist: number }> = [];
    for (const c of live) {
      if (c.at === 'command') continue;
      const u = state.players.find(p => p.id === c.unitId);
      if (!u?.alive) continue;
      const at = this.at(state, f.camera, u.planetIndex, u.surfaceDir, 1.15 * person, w, h);
      if (!at || at.dist > CALLOUT_LOOK.shoulderReach * person || at.x < 0 || at.x > w || at.y < 0 || at.y > h) continue;   // (in view)
      seen.push({ c, x: at.x, y: at.y, dist: at.dist });
    }
    // the most at once: the highest rank, then the nearest; the rest a pip
    seen.sort((a, b) => (RANK.get(b.c.id) ?? 0) - (RANK.get(a.c.id) ?? 0) || a.dist - b.dist);
    const tags = seen.slice(0, CALLOUT_LOOK.shoulderMost).sort((a, b) => a.dist - b.dist), pips = seen.slice(CALLOUT_LOOK.shoulderMost);
    const age = (c: Callout) => (f.tick - c.tick) * PHYS_DT;
    for (const p of pips) {
      const T = CALLOUT_TONE[p.c.tone];
      g.save(); g.globalAlpha = calloutAlpha(age(p.c)); g.beginPath(); g.arc(p.x, p.y - 5, 4, 0, Math.PI * 2);
      g.fillStyle = T.fill === null ? teamColour(p.c.team) : T.text; g.fill(); g.lineWidth = 1.2; g.strokeStyle = 'rgba(0,0,0,0.6)'; g.stroke(); g.restore();
    }
    const rises = stackCallouts(tags.map(t => calloutBox(g, t.c.say, t.c.tone, t.x, t.y)));
    this.shown = tags.length;
    tags.forEach((t, i) => drawCallout(g, t.c.say, t.c.tone, t.x, t.y, teamColour(t.c.team), calloutAlpha(age(t.c)), 1, rises[i]));
  }

  private command(g: CanvasRenderingContext2D, f: CalloutFrame, live: Callout[], w: number, h: number): void {
    const { state } = f, alpha = f.alpha ?? 1, person = PERSON_BODY_HEIGHT * personSetOf(state).k;
    const brains = state.actionBrains ?? {};
    const place = (planetIndex: number, dir: Vec3) => this.at(state, f.camera, planetIndex, dir, 0, w, h);
    const unit = (id: number) => state.players.find(p => p.id === id);
    const rank = (c: Callout) => RANK.get(c.id) ?? 0;
    /** one call a drawn block (squads merged into one block share it): the highest rank, then the newest */
    const over = new Map<string, { c: Callout; x: number; y: number; colour: string }>();
    g.save(); g.globalAlpha = alpha;
    for (const sq of state.squads ?? []) {
      if (!f.teams.includes(sq.team)) continue;
      const block = f.blocks?.get(sq.id);
      const living = sq.unitIds.map(unit).filter((p): p is NonNullable<typeof p> => !!p && p.alive);
      if (!living.length) continue;
      const colour = teamColour(sq.team);
      // the drill and the target: the picked squads, or every squad near the camera
      const lead = living.find(p => p.id === sq.leader) ?? living[0];
      const near = place(lead.planetIndex, lead.surfaceDir);
      if (f.picked?.has(sq.id) || (near && near.dist < DRILL_NEAR_PEOPLE * person)) {
        const step = brains[lead.id]?.lead;
        if (step?.kind === 'bound') {
          const face = place(step.planetIndex, step.face);
          for (const p of living) {
            const at = place(p.planetIndex, p.surfaceDir), post = step.posts.find(([id]) => id === p.id)?.[1];
            if (!at) continue;
            if (step.moving.includes(p.id) && post) { const to = place(step.planetIndex, post); if (to) moveArrow(g, at.x, at.y, to.x, to.y, colour); }
            else if (face) fireFan(g, at.x, at.y, Math.atan2(face.y - at.y, face.x - at.x), 70, 0.3);
          }
        }
        const play = (state.plays ?? []).find(p => p.phase !== 'done' && (p.support.includes(sq.id) || p.assault.includes(sq.id)));
        const o = play?.objective, obj = o ? orderPlace(state, 'pointId' in o ? { kind: 'hold', pointId: o.pointId } : { kind: 'hold', planetIndex: o.planetIndex, surfaceDir: o.surfaceDir }) : null;
        const objAt = obj ? place(obj.planetIndex, obj.surfaceDir) : null;
        if (play && objAt && block) {
          if (play.support.includes(sq.id)) {
            fireFan(g, block.x, block.y, Math.atan2(objAt.y - block.y, objAt.x - block.x), Math.hypot(objAt.x - block.x, objAt.y - block.y), 0.08, 'rgba(255,210,74,0.12)');
            ring(g, objAt.x, objAt.y, 34, play.lifted ? 'rgba(140,220,160,0.75)' : 'rgba(255,170,74,0.8)', play.lifted ? [2, 6] : [5, 5]);
          } else {
            const via = play.via?.[sq.id], viaAt = via && via.state !== 'go' ? place(lead.planetIndex, via.at) : null;
            g.save(); g.strokeStyle = colour; g.lineWidth = 2; g.setLineDash([8, 6]); g.beginPath(); g.moveTo(block.x, block.y);
            if (viaAt) g.lineTo(viaAt.x, viaAt.y);
            g.lineTo(objAt.x, objAt.y); g.stroke(); g.restore();
          }
        }
        // the target: the enemy most of its members shoot at
        const votes = new Map<number, number>();
        for (const p of living) { const t = brains[p.id]?.targetId; if (t !== null && t !== undefined) votes.set(t, (votes.get(t) ?? 0) + 1); }
        let target: number | null = null, most = 0;
        for (const [id, n] of votes) if (n > most || (n === most && target !== null && id < target)) { target = id; most = n; }
        const t = target !== null ? unit(target) : undefined;
        const tAt = t?.alive ? place(t.planetIndex, t.surfaceDir) : null;
        if (tAt && block) targetMark(g, block.x, block.y, tAt.x, tAt.y);
      }
      if (!block) continue;
      const key = `${Math.round(block.x)},${Math.round(block.y)}`;
      for (const c of live) {
        if (c.squadId !== sq.id || c.at === 'shoulder') continue;
        const had = over.get(key);
        if (!had || rank(c) > rank(had.c) || (rank(c) === rank(had.c) && c.tick > had.c.tick)) over.set(key, { c, x: block.x, y: block.y - 20, colour });
      }
    }
    for (const o of over.values()) drawCallout(g, o.c.say, o.c.tone, o.x, o.y, o.colour, calloutAlpha((f.tick - o.c.tick) * PHYS_DT), 0.95);
    this.shown = over.size;
    g.restore();
  }
}

function teamColour(team: number): string { return TEAMS[team % TEAMS.length].primary; }

function moveArrow(g: CanvasRenderingContext2D, ax: number, ay: number, bx: number, by: number, colour: string): void {
  const a = Math.atan2(by - ay, bx - ax), len = Math.hypot(bx - ax, by - ay);
  if (len < 8) return;
  g.save(); g.strokeStyle = colour; g.fillStyle = colour; g.lineWidth = 2; g.lineCap = 'round';
  g.beginPath(); g.moveTo(ax, ay); g.lineTo(bx - 7 * Math.cos(a), by - 7 * Math.sin(a)); g.stroke();
  g.beginPath(); g.moveTo(bx, by); g.lineTo(bx - 10 * Math.cos(a - 0.45), by - 10 * Math.sin(a - 0.45)); g.lineTo(bx - 10 * Math.cos(a + 0.45), by - 10 * Math.sin(a + 0.45)); g.closePath(); g.fill();
  g.restore();
}
function fireFan(g: CanvasRenderingContext2D, x: number, y: number, toward: number, reach: number, half: number, fill = 'rgba(255,210,74,0.18)'): void {
  g.save(); g.fillStyle = fill; g.strokeStyle = 'rgba(255,210,74,0.5)'; g.lineWidth = 1;
  g.beginPath(); g.moveTo(x, y); g.arc(x, y, reach, toward - half, toward + half); g.closePath(); g.fill(); g.stroke(); g.restore();
}
function ring(g: CanvasRenderingContext2D, x: number, y: number, r: number, colour: string, dash: number[]): void {
  g.save(); g.strokeStyle = colour; g.lineWidth = 2; g.setLineDash(dash); g.beginPath(); g.ellipse(x, y, r * 1.4, r, 0, 0, Math.PI * 2); g.stroke(); g.restore();
}
function targetMark(g: CanvasRenderingContext2D, fx: number, fy: number, x: number, y: number): void {
  g.save(); g.strokeStyle = 'rgba(255,120,100,0.5)'; g.lineWidth = 1; g.setLineDash([2, 5]);
  g.beginPath(); g.moveTo(fx, fy); g.lineTo(x, y); g.stroke(); g.setLineDash([]);
  g.strokeStyle = '#ff7864'; g.lineWidth = 2; const r = 11, k = 5;
  for (const [sx, sy] of [[-1, -1], [1, -1], [1, 1], [-1, 1]]) { g.beginPath(); g.moveTo(x + sx * r, y + sy * (r - k)); g.lineTo(x + sx * r, y + sy * r); g.lineTo(x + sx * (r - k), y + sy * r); g.stroke(); }
  g.restore();
}
